// Toasts (DevRobot UI kit): call `toast()` from anywhere, or `useToast()` in components.
// <ToastLayout> renders them inside the router, so messages may contain <Link>.
// They spring in from the corner, pause while hovered and collapse smoothly on exit.

import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Outlet, ScrollRestoration } from 'react-router';
import { CircleCheck, CircleX, Info, Sparkles, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { ease, reducedMotion } from '../kit/motion';
import { haptic } from '../kit/native';

export type ToastTone = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface ToastOptions {
  description?: ReactNode;
  tone?: ToastTone;
  /** Milliseconds before it closes on its own. */
  duration?: number;
  action?: { label: string; onClick: () => void };
  /** Arrives with a tick (errors: double). Pass false for toasts nobody caused, like background status. */
  haptic?: boolean;
}

interface ToastItem extends Required<Pick<ToastOptions, 'tone' | 'duration' | 'haptic'>>, Omit<ToastOptions, 'tone' | 'duration' | 'haptic'> {
  id: number;
  title: ReactNode;
  closing: boolean;
}

const TONES: Record<ToastTone, [LucideIcon, string]> = {
  success: [CircleCheck, 'dr-tint-success'],
  error: [CircleX, 'dr-tint-error'],
  warning: [TriangleAlert, 'dr-tint-warning'],
  info: [Info, 'dr-tint-info'],
  neutral: [Sparkles, 'dr-tint-primary'],
};

const MAX_VISIBLE = 4;

let items: ToastItem[] = [];
let seq = 0;
const subscribers = new Set<() => void>();
const emit = () => subscribers.forEach((fn) => fn());
const subscribe = (fn: () => void) => {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
};
const snapshot = () => items;

/** toast("Saved", { description, tone: "success", action: { label: "Undo", onClick } }). Returns a function that closes it. */
export function toast(title: ReactNode, options: ToastOptions = {}): () => void {
  const tone = options.tone ?? 'neutral';
  const item: ToastItem = { duration: tone === 'error' ? 9000 : 5000, haptic: true, ...options, tone, id: ++seq, title, closing: false };
  if (item.haptic) haptic(tone === 'error' ? 'error' : 'tap');
  const open = items.filter((t) => !t.closing);
  // Keep the stack short: the oldest open toast gives way.
  if (open.length >= MAX_VISIBLE) items = items.map((t) => (t.id === open[0].id ? { ...t, closing: true } : t));
  items = [...items, item];
  emit();
  return () => closeToast(item.id);
}

function closeToast(id: number) {
  items = items.map((t) => (t.id === id ? { ...t, closing: true } : t));
  emit();
}

function removeToast(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

export interface ToastApi {
  success: (message: ReactNode, options?: ToastOptions) => () => void;
  error: (message: ReactNode, options?: ToastOptions) => () => void;
  warning: (message: ReactNode, options?: ToastOptions) => () => void;
  info: (message: ReactNode, options?: ToastOptions) => () => void;
}

const api: ToastApi = {
  success: (m, o) => toast(m, { ...o, tone: 'success' }),
  error: (m, o) => toast(m, { ...o, tone: 'error' }),
  warning: (m, o) => toast(m, { ...o, tone: 'warning' }),
  info: (m, o) => toast(m, { ...o, tone: 'info' }),
};

export function useToast(): ToastApi {
  return api;
}

/** Renders children plus the toast stack (for trees outside the route table, e.g. tests). */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster />
    </>
  );
}

/** Root route element: renders toasts inside the router, so messages may contain <Link>. New pages open at the top. */
export function ToastLayout() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
      <Toaster />
    </>
  );
}

export function Toaster() {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (
    <div
      className="toast toast-end toast-bottom right-0 bottom-0 z-[80] max-w-none items-end gap-2 p-4 pr-[max(1rem,var(--dr-safe-right))] pb-[calc(1rem+var(--dr-dock)+var(--dr-safe-bottom))] sm:p-6 sm:pr-[max(1.5rem,var(--dr-safe-right))] sm:pb-[calc(1.5rem+var(--dr-dock)+var(--dr-safe-bottom))]"
      role="status"
      aria-live="polite"
    >
      {list.map((t) => (
        <ToastCard key={t.id} item={t} />
      ))}
    </div>
  );
}

function ToastCard({ item }: { item: ToastItem }) {
  const ref = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [Icon, tint] = TONES[item.tone] ?? TONES.neutral;

  useLayoutEffect(() => {
    if (reducedMotion() || !ref.current?.animate) return;
    ref.current.animate([{ opacity: 0, transform: 'translateY(18px) scale(.95)' }, { opacity: 1, transform: 'none' }], { duration: 620, easing: ease.spring });
  }, []);

  useEffect(() => {
    if (item.closing || paused) return;
    const timer = setTimeout(() => closeToast(item.id), item.duration);
    return () => clearTimeout(timer);
  }, [item.closing, paused, item.id, item.duration]);

  useEffect(() => {
    if (!item.closing) return;
    const el = ref.current;
    if (!el || reducedMotion() || !el.animate) return removeToast(item.id);
    let cancelled = false;
    void (async () => {
      try {
        await el.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateX(28px) scale(.97)' }], { duration: 220, easing: ease.glide, fill: 'forwards' }).finished;
        await el.animate(
          [
            { height: `${el.offsetHeight}px`, marginTop: '0px' },
            { height: '0px', marginTop: '-0.5rem', paddingBlock: '0px', borderWidth: '0px' },
          ],
          { duration: 260, easing: ease.soft, fill: 'forwards' },
        ).finished;
      } catch {
        /* animation cancelled */
      }
      if (!cancelled) removeToast(item.id);
    })();
    return () => {
      cancelled = true;
    };
  }, [item.closing, item.id]);

  return (
    <div
      ref={ref}
      role={item.tone === 'error' ? 'alert' : undefined}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      className="dr-surface dr-shadow-lg pointer-events-auto flex w-[min(24rem,calc(100vw-2rem))] items-start gap-3 overflow-hidden p-3 pr-2 text-sm whitespace-normal"
    >
      <span className={`${tint} grid size-8 shrink-0 place-items-center rounded-full`}>
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1 py-1">
        <div className="break-anywhere leading-snug font-semibold [&_a]:link [&_a]:text-primary">{item.title}</div>
        {item.description ? <div className="mt-0.5 leading-snug break-anywhere text-base-content/65">{item.description}</div> : null}
      </div>
      {item.action ? (
        <button
          type="button"
          className="btn btn-ghost btn-sm text-primary"
          onClick={() => {
            item.action?.onClick();
            closeToast(item.id);
          }}
        >
          {item.action.label}
        </button>
      ) : null}
      <button type="button" className="btn btn-ghost btn-sm btn-square text-base-content/50" aria-label="Dismiss notification" onClick={() => closeToast(item.id)}>
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
